import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';
import { api } from '../services/api';
import { socket } from '../services/socket';
import { useStore } from '../store';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

interface InviteDetails {
  email: string;
  role: string;
  organization_name: string;
  invited_by_name: string;
  organization_id: string;
  subdomain: string;
}

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setUser = useStore((state) => state.setUser);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite_token');

  const [formData, setFormData] = useState({
    organization_name: '',
    subdomain: '',
    email: '',
    name: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [workspaceAvailability, setWorkspaceAvailability] = useState<{
    checking: boolean;
    available: boolean | null;
    message: string;
  }>({ checking: false, available: null, message: '' });

  useEffect(() => {
    document.title = t('register.title');

    // If there's an invite token, load the invite details
    if (inviteToken) {
      loadInviteDetails();
    }
  }, []);

  const loadInviteDetails = async () => {
    setLoadingInvite(true);
    try {
      const res = await fetch(`${API_URL}/api/team/invite/${inviteToken}`);
      const data = await res.json();

      if (data.success) {
        const invite = data.invitation;
        setInviteDetails(invite);

        // Pre-fill form data
        setFormData(prev => ({
          ...prev,
          organization_name: invite.organization_name,
          subdomain: invite.subdomain,
          email: invite.email,
        }));
      } else {
        setError(data.error || t('acceptInvite.invalid'));
      }
    } catch (err) {
      console.error('Error loading invite:', err);
      setError(t('register.loadingInvite'));
    } finally {
      setLoadingInvite(false);
    }
  };

  // Debounced workspace ID availability check
  useEffect(() => {
    // Skip availability check for invite registrations
    if (inviteDetails) {
      setWorkspaceAvailability({ checking: false, available: true, message: '' });
      return;
    }

    const checkAvailability = async () => {
      const workspaceId = formData.subdomain.trim();

      // Don't check if empty
      if (!workspaceId) {
        setWorkspaceAvailability({ checking: false, available: null, message: '' });
        return;
      }

      // Don't check if too short
      if (workspaceId.length < 2) {
        setWorkspaceAvailability({
          checking: false,
          available: false,
          message: t('register.workspaceMinLength')
        });
        return;
      }

      setWorkspaceAvailability({ checking: true, available: null, message: t('register.checking') });

      try {
        const response = await api.checkWorkspaceAvailability(workspaceId);

        if (response.available) {
          setWorkspaceAvailability({
            checking: false,
            available: true,
            message: t('register.available', { id: response.workspaceId })
          });
        } else {
          setWorkspaceAvailability({
            checking: false,
            available: false,
            message: response.error || t('register.taken', { id: workspaceId })
          });
        }
      } catch (err: any) {
        setWorkspaceAvailability({
          checking: false,
          available: null,
          message: ''
        });
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.subdomain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if workspace is available (only for non-invite registrations)
    if (!inviteDetails && workspaceAvailability.available === false) {
      setError(t('register.workspaceNotAvailable'));
      return;
    }

    // Only require Turnstile if it's configured and loaded
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && turnstileLoaded && !turnstileToken) {
      setError(t('auth.securityCheck'));
      return;
    }

    setLoading(true);

    try {
      // For invited users, send dummy org data (backend will ignore since they'll join via invite)
      // We need to send these to pass validation, but the accept-invite will override the org
      const registrationData = inviteDetails
        ? {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            organization_name: `${inviteDetails.organization_name} (temp)`,
            subdomain: `temp-${Date.now()}`,
            turnstileToken: turnstileToken || undefined,
          }
        : { ...formData, turnstileToken: turnstileToken || undefined };

      const { user, tokens } = await api.register(registrationData);
      setUser(user);
      socket.connect(tokens.accessToken);

      // If this is from an invite, accept it after successful registration
      if (inviteToken && inviteDetails) {
        try {
          const res = await fetch(`${API_URL}/api/team/accept-invite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokens.accessToken}`,
            },
            body: JSON.stringify({
              token: inviteToken,
              userId: user.id,
            }),
          });

          const data = await res.json();

          if (data.success) {
            // Update user's organization in store
            user.organization_id = data.organization_id;
            setUser(user);
          } else {
            console.error('Failed to accept invite:', data.error);
          }
        } catch (inviteErr) {
          console.error('Error accepting invite:', inviteErr);
        }
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvite) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>{t('register.loadingInvite')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex justify-center p-4 py-8 overflow-y-auto">
      <div className="max-w-md w-full my-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-6 border-2 border-gray-200">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>

          <div className="text-center mb-6">
            <img src="/tahc-logo-2026.jpg" alt="tahc" className="w-20 h-20 mx-auto mb-3 object-contain" />
            <p className="text-gray-600 text-base">
              {inviteDetails ? t('register.joinOrg', { org: inviteDetails.organization_name }) : t('register.createAccount')}
            </p>
          </div>

          {inviteDetails && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900 mb-1">
                {t('register.invitedBy', { name: inviteDetails.invited_by_name, org: inviteDetails.organization_name })}
              </p>
              <p className="text-xs text-blue-700">
                {t('register.completeRegistration')}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Hide organization fields for invites */}
            {!inviteDetails && (
              <>
                <div>
                  <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('register.organizationName')}
                  </label>
                  <input
                    id="organization_name"
                    type="text"
                    required
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                    placeholder={t('register.orgPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('register.workspaceId')}
                    <span className="ml-1 text-xs font-normal text-gray-500" title={t('register.workspaceDesc')}>ⓘ</span>
                  </label>
                  <div className="relative">
                    <input
                      id="subdomain"
                      type="text"
                      required
                      value={formData.subdomain}
                      onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className={`w-full px-4 py-3 pr-10 border rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition ${
                        workspaceAvailability.available === true
                          ? 'border-green-500'
                          : workspaceAvailability.available === false
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder={t('register.workspaceIdPlaceholder')}
                    />
                {/* Availability indicator */}
                {formData.subdomain && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    {workspaceAvailability.checking ? (
                      <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-gray-900 rounded-full"></div>
                    ) : workspaceAvailability.available === true ? (
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : workspaceAvailability.available === false ? (
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : null}
                  </div>
                )}
              </div>
              <p className={`mt-0.5 text-xs ${
                workspaceAvailability.available === true
                  ? 'text-green-600'
                  : workspaceAvailability.available === false
                  ? 'text-red-600'
                  : 'text-gray-500'
              }`}>
                {workspaceAvailability.message || t('register.workspaceHelp')}
              </p>
            </div>
              </>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.yourName')}
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder={t('register.namePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!inviteDetails}
                className={`w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition ${
                  inviteDetails ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="john@acme.com"
              />
              {inviteDetails && (
                <p className="mt-1 text-xs text-gray-500">
                  {t('register.emailLocked')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.password')}
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={12}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder={t('register.passwordPlaceholder')}
              />
              <p className="mt-0.5 text-xs text-gray-500">{t('resetPassword.newPasswordLong')}</p>
            </div>

            {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <style>{`
                  .turnstile-wrapper {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    width: 300px;
                    height: 65px;
                  }

                  /* Force all Turnstile elements to have rounded corners */
                  .turnstile-wrapper * {
                    border-radius: 12px !important;
                  }

                  /* Clip the iframe content with rounded corners */
                  .turnstile-wrapper::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border: 2px solid transparent;
                    border-radius: 12px;
                    pointer-events: none;
                    z-index: 10;
                  }
                `}</style>
                <div className="turnstile-wrapper">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileLoaded(true);
                    }}
                    onError={() => {
                      setTurnstileToken(null);
                      setTurnstileLoaded(false);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                  />
                </div>
              </div>
            )}

            <p className="text-xs text-center text-gray-500">
              {t('register.agreeToTerms')}{' '}
              <a href="https://tahc.ai/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">
                {t('register.termsOfService')}
              </a>
            </p>

            <button
              type="submit"
              disabled={
                loading ||
                workspaceAvailability.checking ||
                workspaceAvailability.available === false ||
                Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY && turnstileLoaded && !turnstileToken)
              }
              className="w-full bg-black text-white py-3 rounded-full font-medium hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('register.creatingAccount') : t('register.createAccount')}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-gray-600">
            {t('register.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-gray-900 hover:text-gray-700 font-medium underline">
              {t('register.signIn')}
            </Link>
          </div>

          <div className="mt-4 text-center text-xs text-gray-500">
            <a href="https://tahc.ai" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
              {t('register.learnMore')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
